import { injectable } from 'inversify';
import container from '@core/di';
import BaseStore from '@core/stores/base';
import { observable, makeObservable, runInAction } from 'mobx';
import AssessmentsEvaluationsService, {
  NoteUpdateAction,
} from '@shared/services/assessmentsevaluations';
import { Assessment } from '@shared/models/assessment';
import { AssessmentMatrix, IAssessmentEvaluationResponseData } from '@shared/models/matrix';
import { AssessmentHistory, AssessmentNoteDTO } from '@shared/models/assesmentnotes';
import {
  CategoriesEvaluations,
  DomainsEvaluations,
  BrainFunctionEvaluation,
  Evaluation,
  ChildAgeModel,
  AbilitiesNote,
  AdditionalCriteriaNote,
  EvaluationReviewStatus,
} from '@shared/models/assessmentdetails';
import { ClinicalCriteriaDTO, AdditionalCriteriaType } from '@shared/models/clinicalcriteria';
import {
  AssessmentModelFactory,
  IAssessmentModelFactory,
} from '@Admin/factories/AssessmentModelFactory';
import { AgeLevelType } from '@shared/models/evaluations';
import { Domain } from '@shared/models/assessmentdetails';
import { getMonthsBetweenDates } from '@shared/utils/date';
import { IGetCurrentStatusResponse } from '@shared/types/services';
import { ASSESSMENT_DOMAINS, DOMAINS_LIST } from '@shared/constants/assessmentdomains';
import { ASSESSMENT_OWNER } from '@shared/models/assessment-status';
import { FamiliesStore, PatientsStore } from '@shared/stores/index';
import { AssessmentEvaluationOverview } from './AssessmentEvaluationOverview';
import { FamilyInfo } from '@shared/models/familyInfo';
import { ChildProfile } from '@shared/models/patients';

@injectable()
export default class AssessmentEvaluationStore extends BaseStore<
  Assessment,
  AssessmentsEvaluationsService
> {
  static diToken = Symbol('assessments-evaluations-store');
  private assessmentModelFactory = container.get<IAssessmentModelFactory>(
    AssessmentModelFactory.diToken,
  );
  private familiesStore = container.get<FamiliesStore>(FamiliesStore.diToken);
  private patientsStore = container.get<PatientsStore>(PatientsStore.diToken);

  private _assessmentMatrix: AssessmentMatrix = new AssessmentMatrix({
    brainFunctions: [],
    brainLevels: [],
  });
  private _clinicalCriteria: ClinicalCriteriaDTO[] = [];
  private _existsAssessmentTemplate = false;
  private _assessmentEvaluationModel: CategoriesEvaluations[] = [];
  private _assessmentEvaluationResponseData: IAssessmentEvaluationResponseData[] | undefined =
    undefined;
  private _additionalCriteriasEvaluationModel: AdditionalCriteriaType[] = [];
  private _assessmentHistory?: AssessmentHistory;
  private _flaggedBrainFunctionIds = new Set<number>();
  private _flaggedClinicalCriterionIds = new Set<number>();
  private _ageLevelEight = {} as ChildAgeModel;
  /** required because of default on FE side to sync with BE */
  private _serverAgeLevelEight = {} as ChildAgeModel;
  private _isValidServerAge = false;
  private _isLoading = false;
  private _isValidEvaluation = false;
  private _isValidCriteria = false;
  private _isValidAge = false;
  private _age = 0;
  private _child: ChildProfile | null = null;
  private _assessmentStatus: IGetCurrentStatusResponse | null = null;

  constructor() {
    super({
      service: container.get<AssessmentsEvaluationsService>(AssessmentsEvaluationsService.diToken),
    });
    makeObservable<any>(this, {
      _assessmentMatrix: observable,
      _clinicalCriteria: observable,
      _existsAssessmentTemplate: observable,
      _assessmentEvaluationModel: observable,
      _assessmentEvaluationResponseData: observable,
      _additionalCriteriasEvaluationModel: observable,
      _assessmentHistory: observable,
      _flaggedBrainFunctionIds: observable,
      _flaggedClinicalCriterionIds: observable,
      _ageLevelEight: observable,
      _serverAgeLevelEight: observable,
      _isValidServerAge: observable,
      _isLoading: observable,
      _isValidEvaluation: observable,
      _isValidCriteria: observable,
      _isValidAge: observable,
      _age: observable,
      _child: observable,
      _assessmentStatus: observable,
    });
  }

  get assessmentEvaluationModel() {
    return this._assessmentEvaluationModel;
  }
  get additionalCriteriasEvaluationModel() {
    return this._additionalCriteriasEvaluationModel;
  }
  get childAge() {
    return this._age;
  }
  get child() {
    return this._child;
  }
  get ageLevelEight() {
    return this._ageLevelEight;
  }
  get serverAgeLevelEight() {
    return this._serverAgeLevelEight;
  }
  get isValidServerAge() {
    return this._isValidServerAge;
  }
  get assessmentHistory() {
    return this._assessmentHistory;
  }
  hasFlaggedBrainFunctionHistory = (brainFunctionId: number) =>
    this._flaggedBrainFunctionIds.has(brainFunctionId);

  hasFlaggedClinicalCriterionHistory = (clinicalCriterionId: number) =>
    this._flaggedClinicalCriterionIds.has(clinicalCriterionId);

  get hasFlaggedItems(): boolean {
    const hasFlaggedBrainFunction = this._assessmentEvaluationModel.some(categories =>
      Object.values(categories).some(domains =>
        Object.values(domains).some(d => d.evaluations.some(e => e.evaluation?.isFlagged)),
      ),
    );

    return (
      hasFlaggedBrainFunction ||
      this._additionalCriteriasEvaluationModel.some(item => item.isFlagged)
    );
  }

  get isLoading() {
    return this._isLoading;
  }

  // TODO looks broken - refactor
  get isValidAssessment() {
    return !this._isValidEvaluation || !this._isValidCriteria || !this._isValidAge;
  }

  get assessmentMatrix() {
    return this._assessmentMatrix;
  }

  get assessmentEvaluationReviewData() {
    return this._assessmentEvaluationModel.reduce<Record<number, Evaluation[]>>((acc, cur, i) => {
      const domainEvaluations = Object.values(cur).filter((ev): ev is DomainsEvaluations =>
        Boolean(ev),
      );
      const evaluations = domainEvaluations.reduce<Evaluation[]>((accEv, curEv) => {
        return [
          ...accEv,
          ...Object.values(curEv)
            .map(d => d.evaluations)
            .flat()
            .map(e => e.evaluation),
        ];
      }, []);

      return {
        ...acc,
        [i]: [...evaluations],
      };
    }, {});
  }

  private isEvaluationReviewed = (e: Evaluation | AdditionalCriteriaType) => {
    const status = this._assessmentStatus;

    if (status?.assessmentOwnedBy === ASSESSMENT_OWNER.ClientPrimaryAccount) {
      return e?.reviewStatus === EvaluationReviewStatus.ReviewerCompleted;
    }

    if (
      status?.assessmentOwnedBy === ASSESSMENT_OWNER.Clinician &&
      status?.isSubsequentAssessment
    ) {
      return e?.reviewStatus === EvaluationReviewStatus.EvaluatorCompleted;
    }

    return true;
  };

  get levelsReviewed(): Record<number, boolean> {
    const levels = Object.fromEntries(
      Object.entries(this.assessmentEvaluationReviewData).map(([i, ev]) => [
        i,
        ev.every(this.isEvaluationReviewed),
      ]),
    );

    return {
      ...levels,
      8: this._additionalCriteriasEvaluationModel.every(this.isEvaluationReviewed),
    };
  }

  get isReviewedAssessment() {
    return Object.values(this.levelsReviewed).every(Boolean);
  }

  get domainLevelReviewed(): Record<string, boolean> {
    const result: Record<string, boolean> = {};

    this._assessmentEvaluationModel.forEach((categoriesEvaluations, level) => {
      DOMAINS_LIST.forEach(({ name: domainKey, category }) => {
        const domain = categoriesEvaluations[category]?.[domainKey];

        if (!domain || domain.evaluations.length === 0) {
          return;
        }

        result[`${level}-${domainKey}`] = domain.evaluations
          .map(e => e.evaluation)
          .every(this.isEvaluationReviewed);
      });
    });

    return result;
  }

  get domainsReviewed(): Record<string, boolean> {
    const result: Record<string, boolean> = {};

    Object.values(ASSESSMENT_DOMAINS).forEach(domainKey => {
      result[domainKey] = this._assessmentEvaluationModel.every(
        (_, level) => this.domainLevelReviewed[`${level}-${domainKey}`] ?? true,
      );
    });

    return result;
  }

  getAssessmentTemplate = async () => {
    runInAction(() => {
      this._isLoading = true;
    });

    try {
      const matrix = await this.service.getMatrixAssessments();
      const clinicalCriteria = await this.service.getClinicalCriteria();

      runInAction(() => {
        this._assessmentMatrix = new AssessmentMatrix(matrix);
        this._clinicalCriteria = clinicalCriteria;
        this._existsAssessmentTemplate = true;
      });
    } catch (err) {
      runInAction(() => {
        this._isLoading = false;
      });
      throw err;
    } finally {
      runInAction(() => {
        this._isLoading = false;
      });
    }
  };

  getEvaluations = async (childId: number, assessmentId: number) => {
    try {
      const evaluations = await this.service.getEvaluations(assessmentId);

      runInAction(() => {
        this._assessmentEvaluationResponseData = evaluations;
      });

      const batchHistory = await this.service.getBrainFunctionBatchHistory(childId, assessmentId);

      runInAction(() => {
        const tabs = this.assessmentModelFactory.createBrainFunctionEvaluationModel(
          this._assessmentMatrix.matrix,
          evaluations,
          batchHistory,
        );

        const ageLevelEight = {} as ChildAgeModel;
        const serverAgeLevelEight = {} as ChildAgeModel;
        evaluations.forEach(item => {
          Object.assign(ageLevelEight, {
            [item.brainDomain]: item.functionalAgeAtLevel8,
          });
          Object.assign(serverAgeLevelEight, {
            [item.brainDomain]: item.functionalAgeAtLevel8,
          });
        });

        this._assessmentEvaluationModel = tabs;
        this._ageLevelEight = ageLevelEight;
        this._serverAgeLevelEight = serverAgeLevelEight;
        this._isValidEvaluation = this.isValidAssessmentEvaluation(tabs);
        this._isValidAge = this.isValidChildAge(tabs, ageLevelEight);
        this._isValidServerAge = this.isValidServerChildAge(tabs, serverAgeLevelEight);
      });
    } catch (error) {
      console.log('getEvaluations error', error);

      return Promise.reject(error);
    }
  };

  getAdditionalCriteria = async (childId: number, assessmentId: number) => {
    const batchHistory = await this.service.getClinicalCriteriaBatchHistory(childId, assessmentId);

    try {
      const additionalCriteria = await this.service.getCurrentClinicalCriteria(assessmentId);

      runInAction(() => {
        const additionalTabs = this.assessmentModelFactory.createAdditionalCriteriasEvaluationModel(
          this._clinicalCriteria,
          additionalCriteria,
          batchHistory,
        );

        this._additionalCriteriasEvaluationModel = additionalTabs;

        this._isValidCriteria = this.isValidAdditionalCriteria(additionalTabs);
      });
    } catch (error) {
      runInAction(() => {
        // if there are no assessment related criterias, api returns 404
        const additionalTabs = this.assessmentModelFactory.createAdditionalCriteriasEvaluationModel(
          this._clinicalCriteria,
          [],
          batchHistory,
        );
        this._additionalCriteriasEvaluationModel = additionalTabs;
        this._isValidCriteria = false;
      });
    }
  };

  getCurrentAssessment = async (
    assessmentId: number,
    clientPrimaryAccountId: number,
    childId: number,
    onFetchedClientPrimaryAccount?: (acc: FamilyInfo, child: ChildProfile) => Promise<void>,
  ) => {
    if (!this._existsAssessmentTemplate) {
      await this.getAssessmentTemplate();
    }

    const clientPrimaryAccount = await this.familiesStore.getFamilyInfo(clientPrimaryAccountId);
    const child = await this.patientsStore.getChildProfile(childId);

    runInAction(() => {
      this._child = child;
      this._age = getMonthsBetweenDates(child?.birthDate);
    });

    await onFetchedClientPrimaryAccount?.(clientPrimaryAccount, child);

    runInAction(() => {
      this._isLoading = true;
    });

    try {
      await this.getEvaluations(childId, assessmentId);
      await this.getAdditionalCriteria(childId, assessmentId);
      const status = await this.getAssessmentStatus(clientPrimaryAccountId, childId);

      runInAction(() => {
        this._assessmentStatus = status;
      });
    } finally {
      runInAction(() => {
        this._isLoading = false;
      });
    }
  };

  editEvaluationRating = async (
    clientPrimaryAccountId: number,
    childId: number,
    selectTab: number,
    category: string,
    domain: string,
    evaluation: Evaluation,
  ) => {
    const { brainFunctionId, evaluationRating, prevEvaluationRating, reviewStatus } = evaluation;

    const evaluations = this._assessmentEvaluationModel[selectTab][category][
      domain
    ].evaluations.map(item => {
      if (item.id === brainFunctionId) {
        return {
          ...item,
          evaluation: {
            ...item.evaluation,
            brainFunctionId,
            evaluationRating,
            prevEvaluationRating,
            reviewStatus,
          },
          evaluationHistory: {
            brainFunctionId,
            evaluationRating:
              item?.evaluationHistory?.evaluationRating ?? item.evaluation?.evaluationRating,
          },
        };
      }

      return item;
    });

    const markedAsNotYetAchieved =
      this._assessmentEvaluationModel[selectTab][category][domain].markedAsNotYetAchieved;
    const domainDescription =
      this._assessmentEvaluationModel[selectTab][category][domain].domainDescription;

    runInAction(() => {
      this._assessmentEvaluationModel[selectTab][category][domain] = new Domain(
        evaluations,
        markedAsNotYetAchieved,
        domainDescription,
      );

      this._isValidEvaluation = this.isValidAssessmentEvaluation(this._assessmentEvaluationModel);
      this._isValidAge = this.isValidChildAge(this._assessmentEvaluationModel, this._ageLevelEight);
      this._isValidServerAge = this.isValidServerChildAge(
        this._assessmentEvaluationModel,
        this._serverAgeLevelEight,
      );
    });

    await this.service.editEvaluationRating(clientPrimaryAccountId, childId, {
      brainFunctionEvaluations: [
        {
          brainFunctionId,
          evaluationRating,
          ...(reviewStatus ? { reviewStatus } : {}),
        },
      ],
    });
  };

  reviewBrainFunctions = async (
    clientPrimaryAccountId: number,
    childId: number,
    selectTab: number,
    brainFunctionsIds: number[],
    reviewStatus: EvaluationReviewStatus,
  ) => {
    Object.keys(this._assessmentEvaluationModel[selectTab]).forEach(category => {
      Object.keys(this._assessmentEvaluationModel[selectTab][category]).forEach(domain => {
        const evaluations = this._assessmentEvaluationModel[selectTab][category][
          domain
        ].evaluations.map((item: BrainFunctionEvaluation) => {
          if (brainFunctionsIds.includes(item.id)) {
            return {
              ...item,
              evaluation: { ...item.evaluation, reviewStatus },
            };
          }

          return item;
        });

        const markedAsNotYetAchieved =
          this._assessmentEvaluationModel[selectTab][category][domain].markedAsNotYetAchieved;
        const domainDescription =
          this._assessmentEvaluationModel[selectTab][category][domain].domainDescription;

        runInAction(() => {
          this._assessmentEvaluationModel[selectTab][category][domain] = new Domain(
            evaluations,
            markedAsNotYetAchieved,
            domainDescription,
          );
        });
      });
    });

    await this.service.reviewBrainFunctions(
      clientPrimaryAccountId,
      childId,
      brainFunctionsIds,
      reviewStatus,
    );
  };

  reviewAdditionalCriteria = async (
    clientPrimaryAccountId: number,
    childId: number,
    reviewStatus: EvaluationReviewStatus,
  ) => {
    runInAction(() => {
      this._additionalCriteriasEvaluationModel = this._additionalCriteriasEvaluationModel.map(
        ac => ({
          ...ac,
          reviewStatus,
        }),
      );
    });

    await this.service.reviewAdditionalCriteria(
      clientPrimaryAccountId,
      childId,
      this._additionalCriteriasEvaluationModel.map(ac => ac.clinicalCriterionId),
      reviewStatus,
    );
  };

  editChildAge = async (
    clientPrimaryAccountId: number,
    childId: number,
    ageAtLevel: AgeLevelType,
    // in legacy version request sent even for invalid age
    withInvalidRequest = true,
  ) => {
    runInAction(() => {
      this._ageLevelEight = {
        ...this._ageLevelEight,
        [ageAtLevel.brainDomain]: ageAtLevel.functionalAgeAtLevel8,
      };
      this._isValidAge = this.isValidChildAge(this._assessmentEvaluationModel, this._ageLevelEight);
    });

    if (withInvalidRequest) {
      await this.service.editChildAge(clientPrimaryAccountId, childId, ageAtLevel);
    } else if (!this._isValidAge) {
      await this.service.editChildAge(clientPrimaryAccountId, childId, ageAtLevel);

      runInAction(() => {
        this._serverAgeLevelEight = {
          ...this._serverAgeLevelEight,
          [ageAtLevel.brainDomain]: ageAtLevel.functionalAgeAtLevel8,
        };
        this._isValidServerAge = this.isValidServerChildAge(
          this._assessmentEvaluationModel,
          this._serverAgeLevelEight,
        );
      });
    }
  };

  markDomainAsNotYetAchieved = async (
    assessmentId: number,
    clientPrimaryAccountId: number,
    childId: number,
    selectTab: number,
    category: string,
    domain: string,
  ) => {
    const currentBrainLevelId = selectTab + 1;

    runInAction(() => {
      this._assessmentEvaluationModel[selectTab][category][domain].markedAsNotYetAchieved = true;
    });

    await this.service.markDomainAsNotYetAchieved(
      clientPrimaryAccountId,
      childId,
      domain,
      currentBrainLevelId,
    );

    await this.getEvaluations(childId, assessmentId);
  };

  editAdditionalCriteria = async (
    clientPrimaryAccountId: number,
    childId: number,
    clinicalCriterionOptionID: string,
  ) => {
    const additionalCriteria = this._additionalCriteriasEvaluationModel.map(item => {
      const activeOption = item.options.find(
        option => option.clinicalCriterionOptionID === Number(clinicalCriterionOptionID),
      );
      if (activeOption) {
        return {
          ...item,
          option: activeOption,
          historicalClinicalCriterionOptionId:
            item?.historicalClinicalCriterionOptionId ?? item.option.clinicalCriterionOptionID,
        };
      }

      return item;
    });

    runInAction(() => {
      this._additionalCriteriasEvaluationModel = additionalCriteria;
      this._isValidCriteria = this.isValidAdditionalCriteria(additionalCriteria);
    });

    const ids = additionalCriteria
      .map(item => {
        if (item.option.clinicalCriterionOptionID) {
          return item.option.clinicalCriterionOptionID;
        }
      })
      .filter(Boolean);

    await this.service.editAdditionalCriteria(clientPrimaryAccountId, childId, ids);
  };

  private getLatestNote = (): AssessmentNoteDTO | null =>
    this._assessmentHistory?.notes
      .filter(n => !n.isDeleted)
      .sort(
        (a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime(),
      )?.[0] ?? null;

  private syncAbilitiesNotePreview = (
    selectTab: number,
    category: string,
    domain: string,
    brainFunctionId: number,
    fallbackText?: string,
  ) => {
    const latestNote = this.getLatestNote();
    const domainModel = this._assessmentEvaluationModel[selectTab][category][domain];

    const evaluations = domainModel.evaluations.map(item => {
      if (item.id === brainFunctionId) {
        return {
          ...item,
          note: latestNote?.text ?? fallbackText,
          noteAuthor: latestNote?.author?.fullname,
          noteDeleted: latestNote?.isDeleted,
          noteDate: latestNote?.creationDate,
        };
      }

      return item;
    });

    // Domain instances are not observable in MobX 5, so mutating `.evaluations`
    // does not notify observers — replace the instance like editEvaluationRating does
    runInAction(() => {
      this._assessmentEvaluationModel[selectTab][category][domain] = new Domain(
        evaluations,
        domainModel.markedAsNotYetAchieved,
        domainModel.domainDescription,
      );
    });
  };

  private syncAdditionalCriteriaNotePreview = (
    clinicalCriterionId: number,
    fallbackText?: string,
  ) => {
    const latestNote = this.getLatestNote();

    runInAction(() => {
      this._additionalCriteriasEvaluationModel = this._additionalCriteriasEvaluationModel.map(
        item => {
          if (item.clinicalCriterionId === clinicalCriterionId) {
            return {
              ...item,
              note: latestNote?.text ?? fallbackText ?? '',
              noteAuthor: latestNote?.author?.fullname ?? '',
              noteDeleted: latestNote?.isDeleted ?? false,
              noteDate: latestNote?.creationDate ?? '',
            };
          }

          return item;
        },
      );
    });
  };

  addAbilitiesNote = async (
    assessmentId: number,
    selectTab: number,
    category: string,
    domain: string,
    note: AbilitiesNote,
    childId: number,
  ) => {
    await this.service.addAbilitiesNote(assessmentId, note);

    const history = await this.service.getBrainFunctionHistory(
      childId,
      assessmentId,
      note.brainFunctionId,
    );

    runInAction(() => {
      this._assessmentHistory = history;
      if (history?.flags?.length) {
        this._flaggedBrainFunctionIds.add(note.brainFunctionId);
      }
    });

    this.syncAbilitiesNotePreview(selectTab, category, domain, note.brainFunctionId, note.text);
  };

  updateAbilitiesNote = async (
    assessmentId: number,
    brainFunctionId: number,
    noteId: number,
    updateAction: NoteUpdateAction,
    childId: number,
    selectTab: number,
    category: string,
    domain: string,
    text?: string,
  ) => {
    await this.service.updateAbilitiesNote(
      assessmentId,
      brainFunctionId,
      noteId,
      updateAction,
      text,
    );

    await this.getBrainFunctionHistory(childId, assessmentId, brainFunctionId);

    this.syncAbilitiesNotePreview(selectTab, category, domain, brainFunctionId);
  };

  addAdditionalCriteriaNote = async (
    assessmentId: number,
    note: AdditionalCriteriaNote,
    childId: number,
  ) => {
    await this.service.addAdditionalCriteriaNote(assessmentId, note);

    const history = await this.service.getAdditionalCriteriaHistory(
      childId,
      assessmentId,
      note.clinicalCriterionId,
    );

    runInAction(() => {
      this._assessmentHistory = history;
      if (history?.flags?.length) {
        this._flaggedClinicalCriterionIds.add(note.clinicalCriterionId);
      }
    });

    this.syncAdditionalCriteriaNotePreview(note.clinicalCriterionId, note.text);
  };

  updateAdditionalCriteriaNote = async (
    assessmentId: number,
    clinicalCriterionId: number,
    noteId: number,
    updateAction: NoteUpdateAction,
    childId: number,
    text?: string,
  ) => {
    await this.service.updateAdditionalCriteriaNote(
      assessmentId,
      clinicalCriterionId,
      noteId,
      updateAction,
      text,
    );

    await this.getAdditionalCriteriaHistory(childId, assessmentId, clinicalCriterionId);

    this.syncAdditionalCriteriaNotePreview(clinicalCriterionId);
  };

  getBrainFunctionHistory = async (
    childId: number,
    assessmentId: number,
    brainFunctionId: number,
  ) => {
    const history = await this.service.getBrainFunctionHistory(
      childId,
      assessmentId,
      brainFunctionId,
    );

    runInAction(() => {
      this._assessmentHistory = history;
      if (history?.flags?.length) {
        this._flaggedBrainFunctionIds.add(brainFunctionId);
      }
    });
  };

  getAdditionalCriteriaHistory = async (
    childId: number,
    assessmentId: number,
    clinicalCriterionId: number,
  ) => {
    const history = await this.service.getAdditionalCriteriaHistory(
      childId,
      assessmentId,
      clinicalCriterionId,
    );

    runInAction(() => {
      this._assessmentHistory = history;
      if (history?.flags?.length) {
        this._flaggedClinicalCriterionIds.add(clinicalCriterionId);
      }
    });
  };

  createNewAssessment = async (clientPrimaryAccountId: number, childId: number) => {
    const response = await this.service.createNewAssessment(clientPrimaryAccountId, childId);

    return response.id;
  };

  approveAssessment = async (
    clientPrimaryAccountId: number,
    childId: number,
    chronologicalAgeBasis: 'submission' | 'approval',
  ) => {
    return await this.service.approveAssessment(
      clientPrimaryAccountId,
      childId,
      chronologicalAgeBasis,
    );
  };

  submitAssessment = async (clientPrimaryAccountId: number, childId: number) => {
    return await this.service.submitAssessment(clientPrimaryAccountId, childId);
  };

  /** Submit subsequent assessment created by clinician, review all functions and approve */
  finalizeAssessment = async (
    clientPrimaryAccountId: number,
    childId: number,
    assessmentId: number,
  ) => {
    const evaluations = await this.service.getEvaluations(assessmentId);

    const agesLevelEight = (evaluations as any[])
      .filter(item => !item.functionalAgeAtLevel8 || item.functionalAgeAtLevel8 === '0')
      .map<AgeLevelType>(item => ({
        brainDomain: item.brainDomain,
        functionalAgeAtLevel8: this._age,
      }));

    await this.editChildAgeInManyDomains(clientPrimaryAccountId, childId, agesLevelEight);

    return await this.service.finalizeAssessment(clientPrimaryAccountId, childId);
  };

  editChildAgeInManyDomains = async (
    clientPrimaryAccountId: number,
    childId: number,
    agesAtLevel: AgeLevelType[],
  ) => {
    await Promise.all(
      agesAtLevel.map(async age => this.editChildAge(clientPrimaryAccountId, childId, age, false)),
    );
  };

  getAssessmentStatus = async (
    clientPrimaryAccountId: number,
    childId: number,
  ): Promise<IGetCurrentStatusResponse> => {
    return await this.service.getAssessmentStatus(clientPrimaryAccountId, childId);
  };

  assignAssessmentToClinician = async (
    assessmentId: number,
    clientPrimaryAccountId: number,
    childId: number,
  ) => {
    return await this.service.assignAssessmentToClinician(
      assessmentId,
      clientPrimaryAccountId,
      childId,
    );
  };

  getContent = async (brainFunctionId: number) => {
    return await this.service.getContent(brainFunctionId);
  };

  toggleBrainFunctionFlag = async (
    assessmentId: number,
    selectTab: number,
    category: string,
    domain: string,
    brainFunctionId: number,
    isFlagged: boolean,
  ) => {
    const domainModel = this._assessmentEvaluationModel[selectTab][category][domain];

    const evaluations = domainModel.evaluations.map(item => {
      if (item.id === brainFunctionId) {
        return {
          ...item,
          evaluation: {
            ...item.evaluation,
            isFlagged,
          },
        };
      }

      return item;
    });

    runInAction(() => {
      this._assessmentEvaluationModel[selectTab][category][domain] = new Domain(
        evaluations,
        domainModel.markedAsNotYetAchieved,
        domainModel.domainDescription,
      );
    });

    await this.service.flagBrainFunctionEvaluation(assessmentId, brainFunctionId, isFlagged);
  };

  toggleAdditionalCriterionFlag = async (
    assessmentId: number,
    clinicalCriterionId: number,
    isFlagged: boolean,
  ) => {
    runInAction(() => {
      this._additionalCriteriasEvaluationModel = this._additionalCriteriasEvaluationModel.map(
        item => (item.clinicalCriterionId === clinicalCriterionId ? { ...item, isFlagged } : item),
      );
    });

    await this.service.flagClinicalCriterionEvaluation(
      assessmentId,
      clinicalCriterionId,
      isFlagged,
    );
  };

  isValidAssessmentEvaluation = (data: CategoriesEvaluations[]) => {
    const evaluations: boolean[] = [];

    data.forEach((level: CategoriesEvaluations) => {
      Object.keys(level).forEach((category: string) => {
        const domainsEvaluations = level[category] as DomainsEvaluations;
        Object.keys(domainsEvaluations).forEach((domain: string) => {
          const brainFunctionEvaluation = level[category][domain]
            .evaluations as BrainFunctionEvaluation[];
          brainFunctionEvaluation.forEach((item: BrainFunctionEvaluation) => {
            evaluations.push(Boolean(item.evaluation));
          });
        });
      });
    });

    const result = evaluations.every((elem: boolean) => elem === true);

    return result;
  };

  isValidAdditionalCriteria = (data: AdditionalCriteriaType[]) => {
    const options = data.map((item: AdditionalCriteriaType) =>
      Boolean(Object.keys(item.option).length),
    );

    const result = options.every((elem: boolean) => elem === true);

    return result;
  };

  // TODO looks broken - refactor
  isValidChildAge = (tabs: CategoriesEvaluations[], childAge: ChildAgeModel) => {
    const level = tabs[7];

    return Object.keys(level)
      .map(category =>
        Object.keys(level[category]).map(domain => {
          if (level[category][domain].evaluations.length) {
            const evaluations = level[category][domain].evaluations.map(
              item => item.evaluation?.evaluationRating,
            );
            const evaluationRating = evaluations.every(elem => elem === 'NYA');

            return childAge[domain] <= 72 && !evaluationRating;
          }

          return false;
        }),
      )
      .flatMap(v => v)
      .every(item => item === false);
  };

  isValidServerChildAge = (tabs: CategoriesEvaluations[], childAge: ChildAgeModel) => {
    const level = tabs[7];

    return Object.keys(level)
      .map(category =>
        Object.keys(level[category]).map(domain => {
          if (level[category][domain].evaluations.length) {
            const evaluations = level[category][domain].evaluations.map(
              item => item.evaluation?.evaluationRating,
            );

            if (evaluations.every(elem => elem === 'NYA')) {
              return true;
            }

            return childAge[domain] >= 72;
          }

          return true;
        }),
      )
      .flat()
      .every(Boolean);
  };

  getAssessmentEvaluationOverview = async (
    childId: number,
    assessmentId: number,
    isFirstAssessment: boolean,
  ): Promise<AssessmentEvaluationOverview> => {
    if (this._assessmentMatrix.isEmpty) {
      await this.getAssessmentTemplate();
    }

    await this.getEvaluations(childId, assessmentId);

    return new AssessmentEvaluationOverview(
      this._assessmentEvaluationResponseData ?? [],
      this._age,
      this._assessmentMatrix?.matrix,
      isFirstAssessment,
    );
  };
}
